/**
 * Álgebra I · Nivel 4 — texto de los ítems, español.
 *
 * El texto de un paquete de curso vive junto a los paquetes de ítems, bajo
 * content/lang, y nunca dentro de src/. Misma regla, misma razón: src/ no lleva
 * idioma. El paquete de src/content/packs importa este archivo y el registro lo
 * mezcla detrás de content/lang/items.*.js.
 *
 * ESTILO DE LA CASA. Español técnico simplificado: una palabra significa una
 * cosa, una instrucción por frase, voz activa, menos de veinte palabras. Una
 * situación es UNA frase y nunca dice un número que la matemática no use.
 *
 * UNA PALABRA, UN SIGNIFICADO — el vocabulario que añade este nivel:
 *   barra      la línea sobre una raíz cuadrada
 *   raíz       el número que, multiplicado por sí mismo, da el de bajo la barra
 *   cuadrado   un número multiplicado por sí mismo
 *   paréntesis una de las dos partes de un producto, enseñado en el Nivel 3
 *   cero       una entrada en la que una regla vale cero
 *   giro       la entrada donde una regla al cuadrado deja de bajar y sube
 *   curva      la forma que dibuja una regla al cuadrado
 * «punto de giro» son siempre tres palabras y siempre nombran el par de
 * lecturas del giro. Un «valor» es una salida; una entrada nunca se llama valor.
 */
export default {

  // ---------------------------------------------------------------- preguntas
  'l4.ask.pullOutTheSquare': 'Saca de la barra el cuadrado más grande.',
  'l4.ask.joinTheRoots': 'Une esto en una sola raíz.',
  'l4.ask.clearTheBottom': 'Quita la raíz de abajo.',
  'l4.ask.openAndTidy': 'Abre el paréntesis. Luego une lo que se pueda unir.',
  'l4.ask.writeAsProduct': 'Escribe esto como producto de paréntesis.',
  'l4.ask.areaAsProduct': 'Escribe el área como sus dos lados.',
  'l4.ask.whichComesApart': 'Solo uno de estos se abre. Escribe ese como producto.',
  'l4.ask.whichIsSquareMinusSquare': 'Uno de estos es un cuadrado menos otro cuadrado. Escribe ese como producto.',
  'l4.ask.writeTheQuotient': 'Escribe el resultado de esta división.',
  'l4.ask.writeTheRemainder': '¿Qué sobra?',
  'l4.ask.otherSide': 'Escribe el otro lado.',
  'l4.ask.whereIsItZero': '¿Dónde vale cero?',
  'l4.ask.whichInputsShutIt': '¿Qué entradas lo cierran?',
  'l4.ask.solveIt': 'Halla todos los valores que sirven.',
  'l4.ask.howWideIsIt': '¿Cuánto mide de ancho?',
  'l4.ask.howLongIsTheSide': '¿Cuánto mide un lado?',
  'l4.ask.showTheTurn': 'Escribe esta regla con el punto de giro a la vista.',
  'l4.ask.whereDoesItTurn': '¿Dónde gira esta regla?',
  'l4.ask.lineOfSymmetry': 'Escribe la recta por la que se dobla la curva.',
  'l4.ask.greatestValue': '¿Cuál es el valor mayor que alcanza esta regla?',
  'l4.ask.leastValue': '¿Cuál es el valor menor que alcanza esta regla?',
  'l4.ask.whereItCrossesUpright': '¿Cuál es la salida al principio?',
  'l4.ask.whereItCrossesFlat': '¿Dónde vale cero esta regla?',
  'l4.ask.whichOutputs': '¿Qué salidas da esta regla?',
  'l4.ask.readingAt': 'Las lecturas siguen una regla al cuadrado. ¿Cuál es la lectura en {at}?',
  'l4.ask.ruleTurnsAt': 'El giro está en $\\left({h}, {k}\\right)$. Escribe la regla con él a la vista.',
  'l4.ask.ruleIsZeroAt': 'Esta regla vale cero en {p} y en {q}. Escríbela como producto.',
  'l4.ask.writeWithoutBrackets': 'Escribe esta regla sin paréntesis.',
  'l4.ask.whenDoesItLand': '¿Cuándo llega al suelo?',
  'l4.ask.greatestHeight': '¿Cuál es la altura mayor?',
  'l4.ask.largestArea': '¿Cuál es el área mayor?',
  'l4.ask.bestPriceAndTakings': 'Da el mejor precio y lo que se recauda ahí.',

  // ---------------------------------------------------------------- situaciones
  'l4.ctx.squarePlate': 'Una raíz da el lado de esta placa cuadrada del casco.',
  'l4.ctx.squareBay': 'Una raíz da el lado de esta bahía cuadrada de atraque.',
  'l4.ctx.squarePad': 'Una raíz da el lado de esta plataforma cuadrada de lanzamiento.',
  'l4.ctx.squareVat': 'Una raíz da el lado de esta cuba cuadrada de mezcla.',
  'l4.ctx.sideDeck': 'Una cubierta cuadrada tiene esta área. La letra es un lado.',
  'l4.ctx.sideTile': 'Una baldosa cuadrada tiene esta área. La letra es un lado.',
  'l4.ctx.sideFrame': 'Un marco cuadrado tiene esta área. La letra es un lado.',
  'l4.ctx.sideHatch': 'Una escotilla cuadrada tiene esta área. La letra es un lado.',
  'l4.ctx.braceStrut': 'Este tirante cruza dos puntales que forman un ángulo recto.',
  'l4.ctx.braceMast': 'Este cable cruza un mástil y una cubierta en ángulo recto.',
  'l4.ctx.braceRamp': 'Esta viga cruza una rampa y un suelo en ángulo recto.',
  'l4.ctx.braceHatch': 'Esta barra cruza dos bordes de escotilla en ángulo recto.',
  'l4.ctx.mixRail': 'Dos rieles van uno tras otro, y una raíz da cada largo.',
  'l4.ctx.mixCable': 'Dos cables van uno tras otro, y una raíz da cada largo.',
  'l4.ctx.mixSeam': 'Dos costuras van una tras otra, y una raíz da cada largo.',
  'l4.ctx.mixPipe': 'Dos tubos van uno tras otro, y una raíz da cada largo.',
  'l4.ctx.plotBay': 'Esta bahía es un rectángulo. Todavía no sabemos los lados.',
  'l4.ctx.plotDeck': 'Esta cubierta es un rectángulo. Todavía no sabemos los lados.',
  'l4.ctx.plotField': 'Esta parcela es un rectángulo. Todavía no sabemos los lados.',
  'l4.ctx.plotStore': 'Este almacén es un rectángulo. Todavía no sabemos los lados.',
  'l4.ctx.shareFloor': 'Este suelo es un rectángulo. El área va arriba y un lado abajo.',
  'l4.ctx.shareRoll': 'Este rollo es un rectángulo. El área va arriba y un lado abajo.',
  'l4.ctx.shareTrench': 'Esta zanja es un rectángulo. El área va arriba y un lado abajo.',
  'l4.ctx.shareGrid': 'Esta rejilla es un rectángulo. El área va arriba y un lado abajo.',
  'l4.ctx.gatePair': 'Un motor para cuando las dos lecturas del dial multiplican a cero.',
  'l4.ctx.gateValve': 'Una válvula cierra cuando las dos lecturas de palanca multiplican a cero.',
  'l4.ctx.gateLatch': 'Un pestillo cae cuando las dos lecturas del gancho multiplican a cero.',
  'l4.ctx.gateRelay': 'Un relé abre cuando las dos lecturas de bobina multiplican a cero.',
  'l4.ctx.throwFlare': 'Un cadete dispara una bengala. Esta regla da su altura cada segundo.',
  'l4.ctx.throwPod': 'Una grúa sube una cápsula. Esta regla da su altura cada segundo.',
  'l4.ctx.throwDrone': 'Un dron sube y baja. Esta regla da su altura cada segundo.',
  'l4.ctx.throwBuoy': 'Un cadete lanza una boya. Esta regla da su altura cada segundo.',
  'l4.ctx.landFlare': 'Un cadete dispara una bengala. Cae cuando su altura llega a cero.',
  'l4.ctx.landPod': 'Una grúa suelta una cápsula. Cae cuando su altura llega a cero.',
  'l4.ctx.landDrone': 'Un dron sube y baja. Cae cuando su altura llega a cero.',
  'l4.ctx.landBuoy': 'Un cadete lanza una boya. Cae cuando su altura llega a cero.',
  'l4.ctx.fencePen': 'Un corral tiene una valla de largo fijo. Esta regla da su área.',
  'l4.ctx.fenceYard': 'Un patio tiene una valla de largo fijo. Esta regla da su área.',
  'l4.ctx.fenceRun': 'Una pista tiene una valla de largo fijo. Esta regla da su área.',
  'l4.ctx.fenceLot': 'Un solar tiene una valla de largo fijo. Esta regla da su área.',
  'l4.ctx.priceTicket': 'Un puesto fija un precio de entrada. Esta regla da lo recaudado.',
  'l4.ctx.pricePart': 'Un taller fija un precio de pieza. Esta regla da lo recaudado.',
  'l4.ctx.priceRation': 'Una tienda fija un precio de ración. Esta regla da lo recaudado.',
  'l4.ctx.pricePass': 'Una puerta fija un precio de pase. Esta regla da lo recaudado.',
  'l4.ctx.archSpan': 'Un arco sube y baja. Esta regla da su altura.',
  'l4.ctx.archCable': 'Un cable baja y sube. Esta regla da su altura.',
  'l4.ctx.archRib': 'Una nervadura sube y baja. Esta regla da su altura.',
  'l4.ctx.archDome': 'Una cúpula sube y baja. Esta regla da su altura.',
  'l4.ctx.disputeWatch': 'Dos cadetes de la misma guardia no se ponen de acuerdo en esto.',
  'l4.ctx.disputeBench': 'Dos cadetes en el mismo banco no se ponen de acuerdo en esto.',
  'l4.ctx.disputeCheck': 'Dos cadetes que se revisan no se ponen de acuerdo en esto.',
  'l4.ctx.disputeBay': 'Dos cadetes en la misma bahía no se ponen de acuerdo en esto.',
  'l4.ctx.disputeSurvey': 'Dos cadetes del mismo sondeo no se ponen de acuerdo en esto.',
  'l4.ctx.disputeConsole': 'Dos cadetes en la misma consola no se ponen de acuerdo en esto.',

  // ---------------------------------------------------------------- razones
  'l4.why.largestSquareInside': '{sq} es el cuadrado mayor que divide al número bajo la barra.',
  'l4.why.squareComesOut': 'Un cuadrado bajo la barra sale como su propia raíz, así que sale {m}.',
  'l4.why.frontTimesWhatCameOut': 'Multiplica el {c} de delante por el {m} que salió.',
  'l4.why.shareTheFront': 'Ahora reparte el número de delante entre {p}.',
  'l4.why.addUnderTheBarFirst': 'Suma primero bajo la barra. Una barra cubre toda la suma.',
  'l4.why.sameUnderTheBar': 'Las dos raíces llevan {k} bajo la barra, así que se cuentan juntas.',
  'l4.why.countTheRoots': 'Cuenta las raíces. El número bajo la barra no cambia.',
  'l4.why.takeEachSquareOut': 'Saca primero el cuadrado de cada raíz.',
  'l4.why.timesTheSameRoot': 'Multiplica arriba y abajo por la raíz de {k}.',
  'l4.why.shareTheRootOver': 'La raíz de fuera llega a cada término de dentro.',
  'l4.why.rootTimesItself': 'Una raíz por sí misma da {k}.',
  'l4.why.pairMultipliesAndAdds': 'Una pareja multiplica a {c} y suma {b}.',
  'l4.why.eachNumberItsBracket': 'Cada número de la pareja se lleva su propio paréntesis.',
  'l4.why.constantIsASquare': 'El último término es {p} por {p}.',
  'l4.why.sameBracketTwice': 'Un paréntesis usado dos veces es ese paréntesis al cuadrado.',
  'l4.why.commonFactorFirst': 'Saca primero el {g} de cada término.',
  'l4.why.firstTimesLast': '{a} por {c} es el número al que debe multiplicar la pareja.',
  'l4.why.shareTheFrontNumber': 'Los dos paréntesis se reparten el {a} de delante.',
  'l4.why.frontIsASquare': 'El primer término es el cuadrado de {p} veces la letra.',
  'l4.why.bothPartsAreSquares': 'Las dos partes son cuadrados, y {c} por {c} da el último.',
  'l4.why.onePlusOneMinus': 'Un paréntesis suma y el otro resta.',
  'l4.why.aCubeIsNotASquare': 'Un cubo restado no es un cuadrado restado.',
  'l4.why.plusDoesNotComeApart': 'Dos cuadrados con un más en medio no se separan.',
  'l4.why.zeroMeansTheRuleReadsZero': 'Un cero de una regla es una entrada donde la regla vale cero.',
  'l4.why.noSquareIsBelowZero': 'Ningún cuadrado queda por debajo de cero, así que no sirve ninguna entrada.',
  'l4.why.oneBarOverEachTerm': 'Una barra sobre una suma es una barra sobre cada término.',
  'l4.why.multiplyBackToCheck': 'Multiplica de vuelta. Lo de abajo por la respuesta da lo de arriba.',
  'l4.why.factorTheTopFirst': 'Escribe primero lo de arriba como producto.',
  'l4.why.whatIsLeftOver': 'Al producto le falta esto, así que esto es lo que sobra.',
  'l4.why.thatIsWhatTheBottomNeeds': 'Lo de abajo necesita justo eso para dar lo de arriba.',
  'l4.why.oneBracketIsZero': 'Un producto vale cero cuando un paréntesis vale cero.',
  'l4.why.sameStatementOpenedOut': 'Abierta, la misma frase se lee así.',
  'l4.why.andTheOtherBracket': 'El otro paréntesis da la segunda lectura.',
  'l4.why.everythingOnOneSide': 'Pasa todo a un lado. El otro lado vale ahora cero.',
  'l4.why.nowTheBracketsCanBeZero': 'Ahora sirve la regla de los dos paréntesis, porque el otro lado vale cero.',
  'l4.why.onlyOneWidthCanBeReal': 'Un ancho por debajo de cero no es un ancho, así que una lectura se cae.',
  'l4.why.onlyOneLengthCanBeReal': 'Una longitud por debajo de cero no es una longitud, así que una lectura se cae.',
  'l4.why.rootBothSides': 'Saca la raíz de los dos lados. Un cuadrado tiene dos raíces.',
  'l4.why.twoRootsOneEach': 'Una raíz es {s}. La otra raíz es menos {s}.',
  'l4.why.undoTheShiftLast': 'Pasa el {h} al final, después de la raíz.',
  'l4.why.freeTheSquare': 'Deja primero solo el paréntesis al cuadrado.',
  'l4.why.halfTheMiddleSquared': 'La mitad de {b}, al cuadrado, completa el cuadrado.',
  'l4.why.addItAndTakeItBack': 'Suma {sq} para armar el cuadrado. Luego quita {sq} otra vez.',
  'l4.why.divideTheFrontOutFirst': 'Saca primero el {a} de los dos primeros términos.',
  'l4.why.putTheThreeNumbersIn': 'Pon los tres números en la fórmula.',
  'l4.why.workOutUnderTheRoot': 'La parte bajo la raíz da {disc}.',
  'l4.why.thenTakeTheRoot': 'La raíz de esa parte es {root}.',
  'l4.why.noRootBelowZero': 'Ningún número real elevado al cuadrado da un número por debajo de cero.',
  'l4.why.onlyTheTimeAfterLaunch': 'Solo puede ser real un tiempo posterior al lanzamiento.',
  'l4.why.turnIsMinusBOverTwoA': 'El giro está en menos el número del medio partido por dos veces el primero.',
  'l4.why.readTheRuleAtTheTurn': 'Lee la regla en {h} para tener el valor de ahí.',
  'l4.why.theLineRunsThroughTheTurn': 'La recta pasa vertical por el giro.',
  'l4.why.putZeroIn': 'Pon cero en el lugar de la letra.',
  'l4.why.onlyTheLastTermSurvives': 'Todo término con letra se va, así que queda el último.',
  'l4.why.opensUpSoNothingLower': 'La curva abre hacia arriba, así que nada queda más bajo que el giro.',
  'l4.why.opensDownSoNothingHigher': 'La curva abre hacia abajo, así que nada queda más alto que el giro.',
  'l4.why.secondDifferencesAreEqual': 'Los huecos entre los huecos son iguales, así que encaja una regla al cuadrado.',
  'l4.why.readTheRuleAt': 'Lee esa regla en {at}.',
  'l4.why.vertexFormShape': 'Esta forma deja ver el punto de giro.',
  'l4.why.putTheTurnIn': 'Pon {h} y {k} en esa forma.',
  'l4.why.squareTheBracketFirst': 'Eleva primero el paréntesis al cuadrado.',
  'l4.why.shareThenJoin': 'Reparte el número dentro. Luego une los términos semejantes.',
  'l4.why.aZeroMakesABracket': 'Un cero en {p} da un paréntesis.',
  'l4.why.bothZerosBothBrackets': 'Los dos ceros dan los dos paréntesis.',
};
